import React, { useState, useEffect } from 'react';
import { getIncomingMessages, markMessageProcessed, getUnprocessedMessages, loadSampleMessages, clearAllMessages } from '../services/whatsappWebhookService';
import { parseMessageWithGrok, createDraftLoadFromMessage } from '../services/grokAIService';
import { supabase } from '../supabaseClient';

// Red Alert Panel - Displays incoming messages that need dispatcher attention
// Flags: missing data, low AI confidence, unmatched customers, delay timers, detour violations

function RedAlertPanel({ customers = [], onCreateLoad, onClose }) {
  const [messages, setMessages] = useState([]);
  const [parsedResults, setParsedResults] = useState({});
  const [timers, setTimers] = useState({});
  const [excuseNotes, setExcuseNotes] = useState({});
  const [processing, setProcessing] = useState({});
  const [activeTab, setActiveTab] = useState('unprocessed');
  const [routingAlerts, setRoutingAlerts] = useState([]);
  const [cancelConfirmId, setCancelConfirmId] = useState(null);
  const [clearConfirm, setClearConfirm] = useState(false);

  // Load messages on mount
  useEffect(() => {
    refreshMessages();
    fetchRoutingAlerts();
    // Set up polling for new messages and routing alerts
    const interval = setInterval(() => {
      refreshMessages();
      fetchRoutingAlerts();
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const fetchRoutingAlerts = async () => {
    try {
      const { data: detours, error } = await supabase
        .from('driver_detours')
        .select(`
          *,
          driver:drivers(*, user:users(name, email))
        `)
        .eq('impacts_critical_times', true)
        .eq('status', 'planned')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching routing alerts:', error);
        return;
      }

      const alerts = (detours || []).map(detour => ({
        id: detour.id,
        type: 'detour_violation',
        driverName: detour.driver?.user?.name || detour.driver?.user?.email || 'Unknown Driver',
        driverId: detour.driver_id,
        detourType: detour.detour_type,
        detourAddress: detour.address,
        plannedDuration: detour.planned_duration_minutes,
        impactsCriticalTimes: detour.impacts_critical_times,
        createdAt: detour.created_at,
        notes: detour.notes,
      }));

      setRoutingAlerts(alerts);
    } catch (error) {
      console.error('Error in fetchRoutingAlerts:', error);
    }
  };

  const handleDismissRoutingAlert = async (alertId) => {
    try {
      await supabase
        .from('driver_detours')
        .update({ impacts_critical_times: false })
        .eq('id', alertId);
      
      fetchRoutingAlerts();
    } catch (error) {
      console.error('Error dismissing routing alert:', error);
    }
  };

  const handleCancelDetour = async (alertId) => {
    if (cancelConfirmId !== alertId) { setCancelConfirmId(alertId); return; }
    setCancelConfirmId(null);
    
    try {
      await supabase
        .from('driver_detours')
        .update({ status: 'cancelled' })
        .eq('id', alertId);
      
      fetchRoutingAlerts();
    } catch (error) {
      console.error('Error cancelling detour:', error);
    }
  };

  // Timer countdown effect
  useEffect(() => {
    const timerInterval = setInterval(() => {
      setTimers(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(msgId => {
          if (updated[msgId].remaining > 0) {
            updated[msgId].remaining -= 1;
          }
        });
        return updated;
      });
    }, 1000);
    return () => clearInterval(timerInterval);
  }, []);

  const refreshMessages = () => {
    const allMessages = getIncomingMessages();
    setMessages(allMessages);
  };

  const handleLoadSamples = () => {
    loadSampleMessages();
    setTimeout(refreshMessages, 500);
  };

  const handleClearAll = () => {
    clearAllMessages();
    setParsedResults({});
    setTimers({});
    setExcuseNotes({});
    refreshMessages();
  };

  const handleParseMessage = async (message) => {
    setProcessing(prev => ({ ...prev, [message.id]: true }));
    
    // Find matching customer by phone
    const customer = customers.find(c => 
      c.phone === message.from || 
      c.phone?.replace(/\D/g, '') === message.from?.replace(/\D/g, '')
    );
    
    try {
      const result = await parseMessageWithGrok(message, customer ? { name: customer.company_name } : null);
      result.rawMessage = message.body;
      result.matchedCustomer = customer || null;
      
      setParsedResults(prev => ({ ...prev, [message.id]: result }));
      
      // Check if customer said "give me a sec" - start timer
      if (result.customerIntent === 'delay_notice') {
        startTimer(message.id, 10 * 60); // 10 minutes
      }
    } catch (error) {
      console.error('Parse error:', error);
      setParsedResults(prev => ({ 
        ...prev, 
        [message.id]: { 
          error: error.message, 
          confidence: 0,
          missingFields: ['all']
        } 
      }));
    }
    
    setProcessing(prev => ({ ...prev, [message.id]: false }));
  };

  const startTimer = (messageId, seconds) => {
    setTimers(prev => ({
      ...prev,
      [messageId]: {
        started: new Date().toISOString(),
        duration: seconds,
        remaining: seconds
      }
    }));
  };

  const handleCreateDraftLoad = (message) => {
    const parsed = parsedResults[message.id];
    if (!parsed) return;
    
    const draftLoad = createDraftLoadFromMessage(parsed, parsed.matchedCustomer);
    
    // Mark message as processed
    markMessageProcessed(message.id, { action: 'created_draft', loadNumber: draftLoad.load_number });
    
    if (onCreateLoad) {
      onCreateLoad(draftLoad);
    }
    
    refreshMessages();
  };

  const handleMarkResolved = (message, resolution) => {
    const note = excuseNotes[message.id] || '';
    markMessageProcessed(message.id, { 
      action: resolution, 
      note: note,
      resolvedAt: new Date().toISOString()
    });
    refreshMessages();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getAlertLevel = (parsed) => {
    if (!parsed) return 'unknown';
    if (parsed.error) return 'error';
    if (parsed.confidence < 50) return 'critical';
    if (parsed.confidence < 80 || parsed.missingFields?.length > 2) return 'warning';
    if (!parsed.matchedCustomer) return 'warning';
    return 'ok';
  };

  const getAlertColor = (level) => {
    switch (level) {
      case 'critical': return 'var(--color-danger)';
      case 'warning': return 'var(--color-warning)';
      case 'ok': return 'var(--color-success)';
      case 'error': return 'var(--color-info)';
      default: return '#7f8c8d';
    }
  };

  const unprocessedMessages = messages.filter(m => !m.processed);
  const processedMessages = messages.filter(m => m.processed);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'var(--bg-primary)',
        borderRadius: '10px',
        width: '95%',
        maxWidth: '1200px',
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid var(--border-default)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: 'var(--surface-1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <span style={{ fontSize: '2rem' }}>🚨</span>
            <div>
                <h2 style={{ margin: 0, color: 'var(--text-primary)' }}>Red Alert Panel</h2>
                <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                Incoming messages requiring attention
              </p>
            </div>
            {unprocessedMessages.length > 0 && (
              <span style={{
                backgroundColor: 'var(--color-danger)',
                color: 'var(--text-primary)',
                padding: '0.25rem 0.75rem',
                borderRadius: '15px',
                fontSize: '0.9rem',
                fontWeight: 'bold'
              }}>
                {unprocessedMessages.length} New
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              onClick={handleLoadSamples}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: 'var(--color-info)',
                color: 'var(--text-primary)',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                fontSize: '0.85rem'
              }}
            >
              Load Samples
            </button>
            {clearConfirm ? (
              <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>Clear all?</span>
                <button onClick={() => { handleClearAll(); setClearConfirm(false); }} style={{ padding: '0.4rem 0.6rem', backgroundColor: 'var(--color-danger)', color: 'var(--text-primary)', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}>Yes</button>
                <button onClick={() => setClearConfirm(false)} style={{ padding: '0.4rem 0.6rem', backgroundColor: 'var(--surface-3)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}>No</button>
              </div>
            ) : (
              <button onClick={() => setClearConfirm(true)} style={{ padding: '0.5rem 1rem', backgroundColor: 'var(--surface-3)', color: 'var(--text-primary)', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '0.85rem' }}>Clear All</button>
            )}
            <button
              onClick={onClose}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: 'var(--color-danger)',
                color: 'var(--text-primary)',
                border: 'none',
                borderRadius: '5px',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              Close
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid var(--border-default)',
          backgroundColor: 'var(--surface-1)'
        }}>
          <button
            onClick={() => setActiveTab('unprocessed')}
            style={{
              padding: '1rem 2rem',
              backgroundColor: activeTab === 'unprocessed' ? 'var(--bg-primary)' : 'transparent',
              color: activeTab === 'unprocessed' ? 'var(--accent-primary)' : 'var(--text-muted)',
              border: 'none',
              borderBottom: activeTab === 'unprocessed' ? '2px solid var(--accent-primary)' : 'none',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            Unprocessed ({unprocessedMessages.length})
          </button>
          <button
            onClick={() => setActiveTab('processed')}
            style={{
              padding: '1rem 2rem',
              backgroundColor: activeTab === 'processed' ? 'var(--bg-primary)' : 'transparent',
              color: activeTab === 'processed' ? 'var(--accent-primary)' : 'var(--text-muted)',
              border: 'none',
              borderBottom: activeTab === 'processed' ? '2px solid var(--accent-primary)' : 'none',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            Processed ({processedMessages.length})
          </button>
          <button
            onClick={() => setActiveTab('routing')}
            style={{
              padding: '1rem 2rem',
              backgroundColor: activeTab === 'routing' ? 'var(--bg-primary)' : 'transparent',
              color: activeTab === 'routing' ? 'var(--color-warning)' : 'var(--text-muted)',
              border: 'none',
              borderBottom: activeTab === 'routing' ? '2px solid var(--color-warning)' : 'none',
              cursor: 'pointer',
              fontWeight: 'bold'
            }}
          >
            Routing Alerts ({routingAlerts.length})
            {routingAlerts.length > 0 && (
              <span style={{
                backgroundColor: 'var(--color-danger)',
                color: 'var(--text-primary)',
                padding: '0.1rem 0.4rem',
                borderRadius: '10px',
                fontSize: '0.7rem',
                marginLeft: '0.5rem'
              }}>!</span>
            )}
          </button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflow: 'auto',
          padding: '1rem'
        }}>
          {activeTab === 'routing' && (
            <div>
              {routingAlerts.length === 0 ? (
                <div style={{
                  textAlign: 'center',
                  padding: '3rem',
                  color: 'var(--text-muted)'
                }}>
                  <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🛣️</div>
                  <p>No routing alerts. All drivers are on schedule!</p>
                </div>
              ) : (
                routingAlerts.map(alert => (
                  <div
                    key={alert.id}
                    style={{
                      backgroundColor: 'var(--surface-1)',
                      borderRadius: '8px',
                      padding: '1rem',
                      marginBottom: '1rem',
                      borderLeft: '4px solid var(--color-danger)'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '0.75rem'
                    }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{ fontSize: '1.2rem' }}>⚠️</span>
                          <span style={{ color: 'var(--color-danger)', fontWeight: 'bold' }}>
                            Detour May Cause Late Arrival
                          </span>
                        </div>
                        <div style={{ color: 'var(--accent-primary)', fontWeight: 'bold', marginTop: '0.25rem' }}>
                          Driver: {alert.driverName}
                        </div>
                      </div>
                      <div style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                        {new Date(alert.createdAt).toLocaleString()}
                      </div>
                    </div>

                    <div style={{
                      backgroundColor: 'var(--bg-primary)',
                      padding: '0.75rem',
                      borderRadius: '5px',
                      marginBottom: '0.75rem'
                    }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', fontSize: '0.9rem' }}>
                        <div>
                          <span style={{ color: 'var(--text-muted)' }}>Detour Type: </span>
                          <span style={{ color: 'var(--color-warning)' }}>
                            {alert.detourType === 'fuel' ? '⛽ Fuel Stop' :
                             alert.detourType === 'break' ? '☕ Break' :
                             alert.detourType === 'meal' ? '🍔 Meal Break' :
                             alert.detourType === 'rest' ? '🛏️ Rest Stop' :
                             alert.detourType === 'maintenance' ? '🔧 Maintenance' :
                             alert.detourType === 'weigh_station' ? '⚖️ Weigh Station' :
                             '📍 Other'}
                          </span>
                        </div>
                        <div>
                          <span style={{ color: 'var(--text-muted)' }}>Duration: </span>
                          <span style={{ color: 'var(--text-primary)' }}>{alert.plannedDuration} min</span>
                        </div>
                        {alert.detourAddress && (
                          <div style={{ gridColumn: '1 / -1' }}>
                            <span style={{ color: 'var(--text-muted)' }}>Location: </span>
                            <span style={{ color: 'var(--text-primary)' }}>{alert.detourAddress}</span>
                          </div>
                        )}
                        {alert.notes && (
                          <div style={{ gridColumn: '1 / -1' }}>
                            <span style={{ color: 'var(--text-muted)' }}>Notes: </span>
                            <span style={{ color: 'var(--text-primary)' }}>{alert.notes}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <div style={{
                      backgroundColor: 'rgba(231, 76, 60, 0.2)',
                      padding: '0.5rem 0.75rem',
                      borderRadius: '5px',
                      marginBottom: '0.75rem',
                      fontSize: '0.85rem',
                      color: 'var(--color-danger)'
                    }}>
                      This detour may cause the driver to miss a critical delivery time window. Review and take action.
                    </div>

                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        onClick={() => handleDismissRoutingAlert(alert.id)}
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: 'var(--color-warning)',
                          color: 'var(--bg-primary)',
                          border: 'none',
                          borderRadius: '5px',
                          cursor: 'pointer',
                          fontWeight: 'bold',
                          fontSize: '0.85rem'
                        }}
                      >
                        Acknowledge
                      </button>
                      {cancelConfirmId === alert.id ? (
                        <div style={{ display: 'flex', gap: '0.4rem', alignItems: 'center' }}>
                          <span style={{ color: 'var(--text-secondary)', fontSize: '0.8rem' }}>Cancel detour?</span>
                          <button onClick={() => handleCancelDetour(alert.id)} style={{ padding: '0.4rem 0.6rem', backgroundColor: 'var(--color-danger)', color: 'var(--text-primary)', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.8rem' }}>Yes</button>
                          <button onClick={() => setCancelConfirmId(null)} style={{ padding: '0.4rem 0.6rem', backgroundColor: 'var(--surface-3)', color: 'var(--text-primary)', border: '1px solid var(--border-default)', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem' }}>No</button>
                        </div>
                      ) : (
                        <button onClick={() => setCancelConfirmId(alert.id)} style={{ padding: '0.5rem 1rem', backgroundColor: 'var(--color-danger)', color: 'var(--text-primary)', border: 'none', borderRadius: '5px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.85rem' }}>Cancel Detour</button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {activeTab === 'unprocessed' && unprocessedMessages.length === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '3rem',
              color: 'var(--text-muted)'
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✅</div>
              <p>No unprocessed messages. All clear!</p>
              <button
                onClick={handleLoadSamples}
                style={{
                  marginTop: '1rem',
                  padding: '0.75rem 1.5rem',
                  backgroundColor: 'var(--accent-blue)',
                  color: 'var(--text-primary)',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                Load Sample Messages for Demo
              </button>
            </div>
          )}

          {(activeTab === 'unprocessed' ? unprocessedMessages : processedMessages).map(message => {
            const parsed = parsedResults[message.id];
            const alertLevel = getAlertLevel(parsed);
            const timer = timers[message.id];
            
            return (
              <div
                key={message.id}
                style={{
                  backgroundColor: 'var(--surface-1)',
                  borderRadius: '8px',
                  padding: '1rem',
                  marginBottom: '1rem',
                  borderLeft: `4px solid ${getAlertColor(alertLevel)}`
                }}
              >
                {/* Message Header */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '0.75rem'
                }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ color: 'var(--accent-primary)', fontWeight: 'bold' }}>
                        {message.customerName || 'Unknown Customer'}
                      </span>
                      <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                        {message.from}
                      </span>
                      {message.simulated && (
                        <span style={{
                          backgroundColor: 'var(--color-info)',
                          color: 'var(--text-primary)',
                          padding: '0.1rem 0.5rem',
                          borderRadius: '10px',
                          fontSize: '0.7rem'
                        }}>
                          SIMULATED
                        </span>
                      )}
                    </div>
                    <div style={{ color: '#666', fontSize: '0.8rem' }}>
                      {new Date(message.timestamp).toLocaleString()}
                    </div>
                  </div>
                  
                  {/* Timer display */}
                  {timer && timer.remaining > 0 && (
                    <div style={{
                      backgroundColor: timer.remaining < 60 ? 'var(--color-danger)' : 'var(--color-warning)',
                      color: 'var(--text-primary)',
                      padding: '0.5rem 1rem',
                      borderRadius: '5px',
                      fontWeight: 'bold',
                      animation: timer.remaining < 60 ? 'pulse 1s infinite' : 'none'
                    }}>
                      ⏱️ {formatTime(timer.remaining)}
                    </div>
                  )}
                  
                  {message.processed && (
                    <span style={{
                      backgroundColor: 'var(--color-success)',
                      color: 'var(--text-primary)',
                      padding: '0.25rem 0.75rem',
                      borderRadius: '15px',
                      fontSize: '0.8rem'
                    }}>
                      {message.result?.action || 'Processed'}
                    </span>
                  )}
                </div>

                {/* Message Body */}
                <div style={{
                  backgroundColor: 'var(--bg-primary)',
                  padding: '0.75rem',
                  borderRadius: '5px',
                  marginBottom: '0.75rem',
                  color: 'var(--text-secondary)',
                  whiteSpace: 'pre-wrap'
                }}>
                  {message.body}
                </div>

                {/* Parsed Results */}
                {parsed && (
                  <div style={{
                    backgroundColor: '#0f0f1a',
                    padding: '0.75rem',
                    borderRadius: '5px',
                    marginBottom: '0.75rem'
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '0.5rem'
                    }}>
                      <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>
                        AI Analysis ({parsed.method})
                      </span>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}>
                        <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Confidence:</span>
                        <span style={{
                          color: parsed.confidence >= 80 ? 'var(--color-success)' : 
                                 parsed.confidence >= 50 ? 'var(--color-warning)' : 'var(--color-danger)',
                          fontWeight: 'bold'
                        }}>
                          {parsed.confidence}%
                        </span>
                      </div>
                    </div>

                    {/* Extracted Fields */}
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: '0.5rem',
                      fontSize: '0.85rem'
                    }}>
                      <div>
                        <span style={{ color: 'var(--text-muted)' }}>Pickup: </span>
                        <span style={{ color: parsed.pickupAddress ? 'var(--accent-primary)' : 'var(--color-danger)' }}>
                          {parsed.pickupAddress || 'Missing'}
                        </span>
                      </div>
                      <div>
                        <span style={{ color: 'var(--text-muted)' }}>Delivery: </span>
                        <span style={{ color: parsed.deliveryAddress ? 'var(--accent-primary)' : 'var(--color-danger)' }}>
                          {parsed.deliveryAddress || 'Missing'}
                        </span>
                      </div>
                      <div>
                        <span style={{ color: 'var(--text-muted)' }}>Commodity: </span>
                        <span style={{ color: parsed.commodity ? 'var(--accent-primary)' : 'var(--color-warning)' }}>
                          {parsed.commodity || 'Unknown'}
                        </span>
                      </div>
                      <div>
                        <span style={{ color: 'var(--text-muted)' }}>Weight: </span>
                        <span style={{ color: parsed.weightKg ? 'var(--accent-primary)' : 'var(--color-warning)' }}>
                          {parsed.weightKg ? `${parsed.weightKg} kg` : 'Unknown'}
                        </span>
                      </div>
                      <div>
                        <span style={{ color: 'var(--text-muted)' }}>Customer: </span>
                        <span style={{ color: parsed.matchedCustomer ? 'var(--accent-primary)' : 'var(--color-danger)' }}>
                          {parsed.matchedCustomer?.company_name || 'Not matched'}
                        </span>
                      </div>
                      <div>
                        <span style={{ color: 'var(--text-muted)' }}>Intent: </span>
                        <span style={{ color: 'var(--accent-blue)' }}>
                          {parsed.customerIntent?.replace(/_/g, ' ')}
                        </span>
                      </div>
                    </div>

                    {/* Alerts */}
                    {(parsed.missingFields?.length > 0 || parsed.guessedFields?.length > 0 || !parsed.matchedCustomer) && (
                      <div style={{
                        marginTop: '0.75rem',
                        padding: '0.5rem',
                        backgroundColor: 'var(--bg-primary)',
                        borderRadius: '5px'
                      }}>
                        {parsed.missingFields?.length > 0 && (
                          <div style={{ color: 'var(--color-danger)', fontSize: '0.85rem', marginBottom: '0.25rem' }}>
                            ⚠️ Missing: {parsed.missingFields.join(', ')}
                          </div>
                        )}
                        {parsed.guessedFields?.length > 0 && (
                          <div style={{ color: 'var(--color-warning)', fontSize: '0.85rem', marginBottom: '0.25rem' }}>
                            🤔 Guessed: {parsed.guessedFields.join(', ')}
                          </div>
                        )}
                        {!parsed.matchedCustomer && (
                          <div style={{ color: 'var(--color-danger)', fontSize: '0.85rem' }}>
                            👤 Customer not found in database
                          </div>
                        )}
                      </div>
                    )}

                    {parsed.notes && (
                      <div style={{
                        marginTop: '0.5rem',
                        color: 'var(--text-muted)',
                        fontSize: '0.85rem',
                        fontStyle: 'italic'
                      }}>
                        {parsed.notes}
                      </div>
                    )}
                  </div>
                )}

                {/* Actions */}
                {!message.processed && (
                  <div style={{
                    display: 'flex',
                    gap: '0.5rem',
                    flexWrap: 'wrap',
                    alignItems: 'center'
                  }}>
                    {!parsed ? (
                      <button
                        onClick={() => handleParseMessage(message)}
                        disabled={processing[message.id]}
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: processing[message.id] ? 'var(--surface-3)' : 'var(--accent-blue)',
                          color: 'var(--text-primary)',
                          border: 'none',
                          borderRadius: '5px',
                          cursor: processing[message.id] ? 'not-allowed' : 'pointer'
                        }}
                      >
                        {processing[message.id] ? 'Parsing...' : '🤖 Parse with AI'}
                      </button>
                    ) : (
                      <>
                        {parsed.customerIntent === 'load_request' && parsed.confidence >= 30 && (
                          <button
                            onClick={() => handleCreateDraftLoad(message)}
                            style={{
                              padding: '0.5rem 1rem',
                              backgroundColor: 'var(--color-success)',
                              color: 'var(--text-primary)',
                              border: 'none',
                              borderRadius: '5px',
                              cursor: 'pointer'
                            }}
                          >
                            ✅ Create Draft Load
                          </button>
                        )}
                        <button
                          onClick={() => handleParseMessage(message)}
                          disabled={processing[message.id]}
                          style={{
                            padding: '0.5rem 1rem',
                            backgroundColor: 'var(--color-info)',
                            color: 'var(--text-primary)',
                            border: 'none',
                            borderRadius: '5px',
                            cursor: 'pointer'
                          }}
                        >
                          🔄 Re-parse
                        </button>
                      </>
                    )}
                    
                    <button
                      onClick={() => startTimer(message.id, 10 * 60)}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: 'var(--color-warning)',
                        color: 'var(--text-primary)',
                        border: 'none',
                        borderRadius: '5px',
                        cursor: 'pointer'
                      }}
                    >
                      ⏱️ Start 10min Timer
                    </button>
                    
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: 'auto' }}>
                      <input
                        type="text"
                        placeholder="Will call customer because..."
                        value={excuseNotes[message.id] || ''}
                        onChange={(e) => setExcuseNotes(prev => ({ ...prev, [message.id]: e.target.value }))}
                        style={{
                          padding: '0.5rem',
                          backgroundColor: 'var(--bg-primary)',
                          border: '1px solid var(--border-default)',
                          borderRadius: '5px',
                          color: 'var(--text-primary)',
                          width: '200px'
                        }}
                      />
                      <button
                        onClick={() => handleMarkResolved(message, 'will_call')}
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: '#7f8c8d',
                          color: 'var(--text-primary)',
                          border: 'none',
                          borderRadius: '5px',
                          cursor: 'pointer'
                        }}
                      >
                        📞 Will Call
                      </button>
                      <button
                        onClick={() => handleMarkResolved(message, 'dismissed')}
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: '#555',
                          color: 'var(--text-primary)',
                          border: 'none',
                          borderRadius: '5px',
                          cursor: 'pointer'
                        }}
                      >
                        ✖️ Dismiss
                      </button>
                    </div>
                  </div>
                )}

                {/* Processed result */}
                {message.processed && message.result?.note && (
                  <div style={{
                    marginTop: '0.5rem',
                    color: 'var(--text-muted)',
                    fontSize: '0.85rem'
                  }}>
                    Note: {message.result.note}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Status Bar */}
        <div style={{
          padding: '0.75rem 1.5rem',
          borderTop: '1px solid var(--border-default)',
          backgroundColor: 'var(--surface-1)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: '0.85rem',
          color: 'var(--text-muted)'
        }}>
          <div>
            {Object.values(timers).filter(t => t.remaining > 0).length > 0 && (
              <span style={{ color: 'var(--color-warning)' }}>
                ⏱️ {Object.values(timers).filter(t => t.remaining > 0).length} active timer(s)
              </span>
            )}
          </div>
          <div>
            Last updated: {new Date().toLocaleTimeString()}
          </div>
        </div>
      </div>
    </div>
  );
}

export default RedAlertPanel;
